/**
 * NBM Wishlist — Client-side wishlist using localStorage.
 *
 * Stores an array of product objects under the key 'nbm-wishlist'.
 * Each object: { id, title, url, image, price, compare_at_price, variant_id }
 * Legacy format (array of ID strings) is normalized automatically.
 *
 * Dispatches a custom `wishlist:changed` event on `document` whenever
 * the list is modified.
 *
 * HTML contract:
 *   .js-wishlist-toggle[data-product-id]  — heart / toggle button
 *     Optional data attributes for richer storage:
 *       data-product-title, data-product-url, data-product-image,
 *       data-product-price, data-product-compare-price, data-product-variant-id
 *     Inside, the element (or an SVG/icon child) receives:
 *       .is-wishlisted   class when the product is in the list
 *       fill/stroke      style for SVG hearts
 *
 * Global namespace: window.NBMWishlist
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Constants                                                          */
  /* ------------------------------------------------------------------ */

  const STORAGE_KEY = 'nbm-wishlist';

  /* ------------------------------------------------------------------ */
  /*  Internal helpers                                                   */
  /* ------------------------------------------------------------------ */

  /** Read and normalize the wishlist array from localStorage. */
  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Normalize legacy: convert plain string/number IDs to minimal objects
          return parsed.map(item =>
            (typeof item === 'string' || typeof item === 'number')
              ? { id: String(item) }
              : item
          );
        }
      }
    } catch (error) { /* corrupted data — reset */ }
    return [];
  }

  /** Persist the wishlist array to localStorage. */
  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) { /* storage full */ }
  }

  /** Dispatch the custom event so other scripts can react. */
  function dispatchChanged() {
    const evt = new CustomEvent('wishlist:changed', { detail: { items: readList() } });
    document.dispatchEvent(evt);
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Toggle a product in the wishlist. Adds it if absent, removes if present.
   *
   * @param {string|number} productId
   * @param {object} [productData] — Full product object to store. Falls back to { id } only.
   * @returns {boolean} — true if product is now in wishlist, false if removed.
   */
  function toggle(productId, productData) {
    productId = String(productId);
    const variantId = productData && productData.variant_id ? String(productData.variant_id) : null;
    const list = readList();

    // Match by variant_id when available, otherwise by product id
    const idx = variantId
      ? list.findIndex(product => String(product.variant_id) === variantId)
      : list.findIndex(product => String(product.id) === productId && !product.variant_id);

    if (idx === -1) {
      list.push(productData || { id: productId });
    } else {
      list.splice(idx, 1);
    }

    writeList(list);
    updateAllIcons();
    dispatchChanged();

    return idx === -1; // true = added
  }

  /**
   * Check whether a product is in the wishlist.
   *
   * @param {string|number} productId
   * @returns {boolean}
   */
  function isInWishlist(productId, variantId) {
    const list = readList();
    if (variantId) return list.some(product => String(product.variant_id) === String(variantId));
    return list.some(product => String(product.id) === String(productId));
  }

  /**
   * Return all wishlisted product objects.
   *
   * @returns {object[]}
   */
  function getAll() {
    return readList();
  }

  /**
   * Remove a product from the wishlist (no-op if not present).
   *
   * @param {string|number} productId
   */
  function remove(productId, variantId) {
    const list = readList();
    const idx = variantId
      ? list.findIndex(product => String(product.variant_id) === String(variantId))
      : list.findIndex(product => String(product.id) === String(productId));
    if (idx !== -1) {
      list.splice(idx, 1);
      writeList(list);
      updateAllIcons();
      dispatchChanged();
    }
  }

  /**
   * Return the number of wishlisted products.
   *
   * @returns {number}
   */
  function count() {
    return readList().length;
  }

  /* ------------------------------------------------------------------ */
  /*  Icon Sync                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Walk every `.js-wishlist-toggle` on the page and set the visual state
   * based on whether its `data-product-id` is in the wishlist.
   */
  function updateAllIcons() {
    const list = readList();
    const productIds = new Set(list.map(product => String(product.id)));
    const variantIds = new Set(list.filter(product => product.variant_id).map(product => String(product.variant_id)));
    const buttons = document.querySelectorAll('.js-wishlist-toggle');

    for (const btn of buttons) {
      const pid = String(btn.getAttribute('data-product-id'));
      const vid = btn.getAttribute('data-product-variant-id');
      const active = vid ? variantIds.has(String(vid)) : productIds.has(pid);

      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', String(active));

      // Handle SVG hearts inside the button
      const svg = btn.querySelector('svg');
      if (svg) {
        svg.style.fill = active ? '#e11d48' : 'none';
        svg.style.stroke = active ? '#e11d48' : 'currentColor';
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Event Delegation                                                   */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.js-wishlist-toggle');
    if (!btn) return;
    event.preventDefault();

    const pid = btn.getAttribute('data-product-id');
    if (!pid) {
      console.warn('[NBMWishlist] .js-wishlist-toggle missing data-product-id');
      return;
    }

    const handle = btn.getAttribute('data-product-handle') ||
      (() => {
        const url = btn.getAttribute('data-product-url') || '';
        const match = url.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      })();

    const variantId = btn.getAttribute('data-product-variant-id') || undefined;
    toggle(pid, { id: pid, handle, variant_id: variantId });
  });

  /* ------------------------------------------------------------------ */
  /*  Init — sync icons on page load                                     */
  /* ------------------------------------------------------------------ */

  function init() {
    updateAllIcons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ------------------------------------------------------------------ */
  /*  Export                                                              */
  /* ------------------------------------------------------------------ */

  window.NBMWishlist = {
    toggle,
    isInWishlist,
    getAll,
    remove,
    count,
    updateAllIcons
  };
})();
